import * as Sentry from '@sentry/vue';
import type { App } from 'vue';

type SentryRouter = NonNullable<Parameters<typeof Sentry.browserTracingIntegration>[0]>['router'];

interface SentryInitOptions {
	app: App;
	dsn: string;
	appName: string;
	router?: SentryRouter;
	environment?: string;
	apiUrls?: Array<string | undefined | null>;
	excludedUrls?: Array<string | undefined | null>;
}

/** rrweb can throw this when MutationObserver records touch cross-origin iframe nodes. */
export const SENTRY_RRWEB_CROSS_ORIGIN_PARENT_NODE_ERROR =
	/Permission denied to access property ["']parentNode["']/i;

function getUrlOrigin(raw: string): string | undefined {
	try {
		return new URL(raw.trim()).origin;
	} catch {
		return undefined;
	}
}

function getUrlPrefix(raw: string): string | undefined {
	try {
		const url = new URL(raw.trim());
		return `${url.origin}${url.pathname.replace(/\/$/, '')}`;
	} catch {
		const trimmed = raw.trim().replace(/\/$/, '');
		return trimmed || undefined;
	}
}

/** Build Sentry trace targets from configured API base URLs, excluding auth endpoints like Keycloak. */
export function buildTracePropagationTargets(
	apiUrls: Array<string | undefined | null>,
	excludedUrls: Array<string | undefined | null> = [],
): string[] {
	const excludedOrigins = new Set(
		excludedUrls.flatMap((url) => {
			if (!url) {
				return [];
			}

			const origin = getUrlOrigin(url);
			return origin ? [origin] : [];
		}),
	);

	const targets = new Set<string>();
	for (const raw of apiUrls) {
		if (!raw) {
			continue;
		}

		const prefix = getUrlPrefix(raw);
		const origin = getUrlOrigin(raw);
		if (!prefix || (origin && excludedOrigins.has(origin))) {
			continue;
		}

		targets.add(prefix);
	}

	return [...targets];
}

export function setSentryAppTag(appName: string): void {
	Sentry.getCurrentScope().setTag('app', appName);
}

export function attachEmbeddedSentry(app: App, appName: string): void {
	if (!Sentry.getClient()) {
		return;
	}

	setSentryAppTag(appName);
	Sentry.attachErrorHandler(app);
}

export function initSentry({
	app,
	dsn,
	appName,
	router,
	environment,
	apiUrls = [],
	excludedUrls = [],
}: SentryInitOptions): void {
	if (Sentry.getClient()) {
		attachEmbeddedSentry(app, appName);
		return;
	}

	if (!dsn) {
		return;
	}

	const tracePropagationTargets = buildTracePropagationTargets(apiUrls, excludedUrls);

	Sentry.init({
		app,
		dsn,
		environment: environment ?? 'production',
		integrations: [
			...(router ? [Sentry.browserTracingIntegration({ router })] : []),
			// Block iframes so rrweb MutationObserver never walks cross-origin frames
			// (Firefox/WebView: "Permission denied to access property parentNode").
			Sentry.replayIntegration({
				block: ['iframe'],
			}),
		],
		// Still drop if a host/extension injects a cross-origin node into mutation records.
		ignoreErrors: [SENTRY_RRWEB_CROSS_ORIGIN_PARENT_NODE_ERROR],
		tracesSampleRate: 1.0,
		...(tracePropagationTargets.length > 0 ? { tracePropagationTargets } : {}),
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		enableLogs: true,
		initialScope: {
			tags: { app: appName },
		},
	});
}

export function setSentryUser(user: {
	id: string;
	username?: string | null;
	email?: string | null;
}): void {
	Sentry.setUser({
		id: user.id,
		username: user.username ?? undefined,
		email: user.email ?? undefined,
	});
}

export function clearSentryUser(): void {
	Sentry.setUser(null);
}

/** Thin wrapper so apps can report without depending on `@sentry/vue` directly. */
export function captureSentryMessage(
	message: string,
	captureContext?: Parameters<typeof Sentry.captureMessage>[1],
): string {
	return Sentry.captureMessage(message, captureContext);
}

/** Thin wrapper so apps can report exceptions without depending on `@sentry/vue` directly. */
export function captureSentryException(
	error: unknown,
	captureContext?: Parameters<typeof Sentry.captureException>[1],
): string {
	return Sentry.captureException(error, captureContext);
}
